// CustomMailchimpHeaderMappingModal.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import {
  Dialog,
  Typography,
  Slide,
  DialogContent,
  Select,
  MenuItem,
  DialogActions,
  CircularProgress,
  TextField,
  Chip, // add
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete"; //  add
import { Close, Delete, Add } from "@material-ui/icons";
import MaterialButton from "../../ MaterialButton/MaterialButton";
import ButtonWithSpinner from "../../ButtonWithSpinner/ButtonWIthSpinner";
import "../../../styles/components/headerMapping.scss";
import {
  getCcbCustomFields,
  getMailchimpAudience,
  getMailchimpMergeFields,
} from "../../../store/middlewares/integrationMiddleware";
import { formDataWithApiKey } from "../../../helpers";
import {
  getCustomHeaderMapping,
  saveMcCustomHeaderMapping,
} from "../../../store/middlewares/toolsMiddleware";
import { clearCustomMCHeaderMapping } from "../../../store/actions/toolsActions";
import { setSnackbarData } from "../../../store/actions/globalAction";
import MailchimpIcon from "../../../assets/img/mailchimp/mailchimp-small.png";
import CcbIcon from "../../../assets/img/ccb/ccb-small.png";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DEFAULT_ROW = {
  mcMergeField: "Please Select",
  ccbField: "Please Select",
};

const GROUP_MC_SELECT = "Select MC merge field";
const GROUP_CCB_SELECT = "Select CCB merge field";
const GROUP_CREATE_NEW = "Create a new Field";
const GROUP_CREATE_DUP = "Create a Duplicate Field";

const CustomMailchimpHeaderMappingModal = (props) => {
  const {
    history,
    location,

    // Redux actions
    getMailchimpAudience,
    getMailchimpMergeFields,
    getCcbCustomFields,
    getCustomHeaderMapping,
    saveMcCustomHeaderMapping,
    clearCustomMCHeaderMapping,
    setSnackbarData,

    // Redux props
    mailChimpAudience,
    ccbCustomFields,
    mailchimpMergeFields,
    customHeaderMappings,

    // statuses
    mailChimpAudienceStatus,
    ccbCustomFieldsStatus,
    mailchimpMergeFieldsStatus,
    saveMcCustomHeaderMappingStatus,
    customHeaderMappingsStatus,
  } = props;

  const [audienceId, setAudienceId] = useState("Please Select");

  // Start with NO rows. We only show rows after audience is selected.
  const [rows, setRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState({});

  //  MC existing fields: grouped under "Select MC merge field"
  const mcExistingOptions = useMemo(() => {
    if (!Array.isArray(mailchimpMergeFields) || !mailchimpMergeFields.length)
      return [];

    return mailchimpMergeFields
      .map((f) => {
        const code = f?.tag;
        const name = f?.name || f?.label || code;
        if (!code) return null;
        return {
          code: String(code),
          name: String(name),
          groupBy: GROUP_MC_SELECT,
        };
      })
      .filter(Boolean);
  }, [mailchimpMergeFields]);

  const mcTagToNameMap = useMemo(() => {
    const map = new Map();
    mcExistingOptions.forEach((o) => {
      map.set(String(o.code), String(o.name));
    });
    return map;
  }, [mcExistingOptions]);

  const toggleModal = useCallback(() => {
    history.push("/tools");
  }, [history]);

  // Get initial data on mount
  useEffect(() => {
    const formData = formDataWithApiKey();
    getMailchimpAudience(formData);
    getCcbCustomFields(formData);
  }, [getCcbCustomFields, getMailchimpAudience]);

  useEffect(() => {
    const fromState = location?.state?.audienceId;

    if (!fromState) return;

    // Only set if not already set
    setAudienceId(String(fromState));
    setRows([]); // hide rows until mapping loads
    setErrorMessage((prev) => ({ ...prev, audience: "" }));

    // avoid showing old audience mapping
    clearCustomMCHeaderMapping();
  }, [location?.state?.audienceId, clearCustomMCHeaderMapping]);

  // Fetch MC merge fields + custom header mappings only after audience selection
  useEffect(() => {
    if (!audienceId || audienceId === "Please Select") return;

    const formData = formDataWithApiKey();
    formData.append("mailchimpListId", audienceId);

    getMailchimpMergeFields(formData);
    getCustomHeaderMapping(formData);
  }, [audienceId, getCustomHeaderMapping, getMailchimpMergeFields]);

  // When customHeaderMappings loads/changes, hydrate rows.
  // If mapping exists, show those rows. If not, show ONE empty row.
  useEffect(() => {
    if (!audienceId || audienceId === "Please Select") return;

    if (Array.isArray(customHeaderMappings) && customHeaderMappings.length) {
      const mappedRows = customHeaderMappings.map((m) => {
        const mcField = m?.mailchimpField || "Please Select";
        const ccbField = m?.ccbField || "Please Select";

        // if backend returns MC tag like SECURESEARCH_STATUS,
        // we can show the friendly name from merge fields list
        const mcDisplayName = mcTagToNameMap.get(String(mcField)) || mcField;

        return {
          mcMergeField: mcField,
          ccbField,
          mcDisplayName, // store display label (optional but useful)
        };
      });

      setRows(mappedRows.length ? mappedRows : [DEFAULT_ROW]);
      return;
    }

    setRows([DEFAULT_ROW]);
  }, [audienceId, customHeaderMappings, mcTagToNameMap]);

  const audienceOptions = useMemo(() => {
    const base = [{ id: "Please Select", name: "Audience List" }];

    if (!Array.isArray(mailChimpAudience) || !mailChimpAudience.length)
      return base;

    return [
      ...base,
      ...mailChimpAudience.map((a) => ({
        id: a?.id,
        name: a?.name || a?.id,
      })),
    ];
  }, [mailChimpAudience]);

  //  CCB options: no default option, grouped under "Select CCB merge field"
  const ccbFieldOptions = useMemo(() => {
    if (!Array.isArray(ccbCustomFields) || !ccbCustomFields.length) return [];

    return ccbCustomFields
      .filter((f) => f?.label)
      .map((f) => ({
        code: String(f.label),
        name: String(f.label),
        admin_only: f.admin_only,
        groupBy: GROUP_CCB_SELECT,
      }));
  }, [ccbCustomFields]);

  const normalize = useCallback(
    (v) =>
      String(v || "")
        .trim()
        .toLowerCase(),
    []
  );

  const handleAudienceChange = useCallback(
    (event) => {
      const nextAudienceId = event.target.value;

      setAudienceId(nextAudienceId);

      // fully hide merge fields section until a valid audience is selected
      if (!nextAudienceId || nextAudienceId === "Please Select") {
        setRows([]);
        clearCustomMCHeaderMapping(); // avoid showing previous audience mapping
      } else {
        // temporarily show nothing while mappings load; rows will be hydrated by effect above
        setRows([]);
      }

      setErrorMessage((prev) => ({
        ...prev,
        audience: "",
      }));
    },
    [clearCustomMCHeaderMapping]
  );

  const handleRowChange = useCallback(
    (index, key, value) => {
      const next = [...rows];
      next[index] = { ...next[index], [key]: value };
      setRows(next);

      setErrorMessage((prev) => ({
        ...prev,
        [`${index}_${key}`]: "",
      }));
    },
    [rows]
  );

  const handleAddRow = useCallback(() => {
    setRows((prev) => [...prev, DEFAULT_ROW]);
  }, []);

  const handleDeleteRow = useCallback(
    (index) => {
      const next = rows.filter((_, i) => i !== index);
      // If user deletes everything, keep one empty row (only when audience is selected)
      setRows(next.length ? next : [DEFAULT_ROW]);
    },
    [rows]
  );

  const validate = useCallback(() => {
    let errors = {};

    if (!audienceId || audienceId === "Please Select") {
      errors.audience = "Please select an audience";
    }

    rows.forEach((r, idx) => {
      if (!r.mcMergeField || r.mcMergeField === "Please Select") {
        errors[`${idx}_mcMergeField`] = "Please select a MC merge field";
      }
      if (!r.ccbField || r.ccbField === "Please Select") {
        errors[`${idx}_ccbField`] = "Please select a CCB field";
      }
    });

    setErrorMessage(errors);
    return !Object.values(errors).some((v) => v);
  }, [audienceId, rows]);

  const handleSave = useCallback(() => {
    const ok = validate();
    if (!ok) return;

    const formData = formDataWithApiKey();
    formData.append("mailchimpListId", audienceId);

    rows.forEach((r) => {
      if (!r?.mcMergeField || r.mcMergeField === "Please Select") return;

      // if user picked "Create a new Field" / "Create a Duplicate Field"
      // we added that option with code === selected CCB label.
      // in that case, send mc side as "new"
      const isCreate =
        r.ccbField &&
        r.ccbField !== "Please Select" &&
        String(r.mcMergeField) === String(r.ccbField);

      const mcKey = isCreate ? "new" : r.mcMergeField;

      formData.append(
        `params[mapping][${mcKey}]`,
        r.ccbField === "Please Select" ? "" : r.ccbField
      );
    });

    saveMcCustomHeaderMapping(formData);
  }, [audienceId, rows, saveMcCustomHeaderMapping, validate]);

  useEffect(() => {
    if (saveMcCustomHeaderMappingStatus === "success") {
      toggleModal();
      clearCustomMCHeaderMapping();
      setSnackbarData({
        snackbarSeverity: "success",
        showSnackbar: true,
        snackbarMessage: "Custom Mailchimp header mapping saved successfully.",
      });
    }
  }, [
    clearCustomMCHeaderMapping,
    saveMcCustomHeaderMappingStatus,
    setSnackbarData,
    toggleModal,
  ]);

  const selectedMcFields = useMemo(
    () =>
      rows.map((r) => r.mcMergeField).filter((v) => v && v !== "Please Select"),
    [rows]
  );

  const isAudienceLoading = mailChimpAudienceStatus === "loading";
  const isCcbFieldsLoading = ccbCustomFieldsStatus === "loading";
  const isMergeFieldsLoading = mailchimpMergeFieldsStatus === "loading";
  const isSaving = saveMcCustomHeaderMappingStatus === "loading";

  const isCustomHeaderMappingLoading =
    customHeaderMappingsStatus === "loading" || false;

  const isInitialLoading = isAudienceLoading || isCcbFieldsLoading;
  const isAudienceSelected = audienceId && audienceId !== "Please Select";

  const shouldShowMappingSection = isAudienceSelected && !isMergeFieldsLoading;
  const isMappingSectionLoading =
    isMergeFieldsLoading || isCustomHeaderMappingLoading;

  //  helper to build MC options per-row based on selected CCB field
  const getMcOptionsForRow = useCallback(
    (row) => {
      const ccb = row?.ccbField;
      if (!ccb || ccb === "Please Select") return mcExistingOptions;

      const exists = mcExistingOptions.some(
        (o) =>
          normalize(o.name) === normalize(ccb) ||
          normalize(o.code) === normalize(ccb)
      );

      const createOption = {
        code: String(ccb), // keep value as the label so save() sends it
        name: String(ccb),
        groupBy: exists ? GROUP_CREATE_DUP : GROUP_CREATE_NEW,
        isCreate: true,
      };

      // put create group first (like your screenshot), then existing options
      return [...mcExistingOptions, createOption];
    },
    [mcExistingOptions, normalize]
  );

  //  helper: resolve stored value(string) -> option object for Autocomplete value
  const resolveOptionByCode = useCallback((options, code, fallbackName) => {
    if (!code || code === "Please Select") return null;

    const found = options.find((o) => String(o.code) === String(code)) || null;
    if (found) return found;

    // if API returns tag not in current options, still show it as selected
    return {
      code: String(code),
      name: String(fallbackName || code),
      groupBy: GROUP_MC_SELECT,
    };
  }, []);

  return (
    <div>
      <Dialog
        open={true}
        onClose={toggleModal}
        TransitionComponent={Transition}
        classes={{ paper: "w-100" }}
        maxWidth="md"
      >
        <DialogContent>
          <div className="c-mc-hm__header">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="h5">
                Custom Pushpay (CCB) Field Mapping
              </Typography>

              <Chip
                label="BETA"
                size="small"
                style={{
                  backgroundColor: "#2db8e8",
                  color: "#fff",
                  fontWeight: 400,
                  height: 22,
                }}
              />
            </div>
            <Close className="close-icon mr-2" onClick={toggleModal} />
          </div>

          {/* Audience */}
          <div className="c-mc-hm__audience-row mt-4">
            <div className="mr-2">
              <Typography variant="body1">Audience</Typography>
            </div>

            <div className="c-mc-hm__audience-select">
              {errorMessage.audience && (
                <span className="text-danger c-mc-hm__error">
                  {errorMessage.audience}
                </span>
              )}

              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <Select
                  variant="outlined"
                  value={audienceId}
                  onChange={handleAudienceChange}
                  className="w-100 select-box"
                  disabled={isAudienceLoading}
                  style={{ height: 40 }}
                >
                  {audienceOptions.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>

                {isAudienceLoading && <CircularProgress size={18} />}
              </div>
            </div>
          </div>

          {!isAudienceSelected && !isInitialLoading && (
            <div className="mt-4">
              <Typography variant="body2" className="text-muted">
                Please select an audience to view and edit header mappings.
              </Typography>
            </div>
          )}

          {isMappingSectionLoading && !shouldShowMappingSection ? (
            <div
              className="d-flex align-items-center justify-content-center mt-4"
              style={{ minHeight: 120 }}
            >
              <CircularProgress />
            </div>
          ) : null}

          {/* Merge Fields / CCB Fields (HIDDEN until audience selected) */}
          {shouldShowMappingSection && !isInitialLoading && (
            <div className="c-mc-hm__mapping mt-4">
              <div className="c-mc-hm__mapping-header d-flex align-items-center justify-content-between">
                {isMappingSectionLoading && (
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" className="text-muted">
                      Loading...
                    </Typography>
                  </div>
                )}
              </div>

              {isMappingSectionLoading && rows.length === 0 ? (
                <div
                  className="d-flex align-items-center justify-content-center mt-4"
                  style={{ minHeight: 80 }}
                >
                  <CircularProgress size={22} />
                </div>
              ) : (
                <>
                  <div className="c-mc-hm__rows mt-3">
                    {rows.map((row, index) => {
                      const mcOptionsForRow = getMcOptionsForRow(row);
                      const ccbValueOption = resolveOptionByCode(
                        ccbFieldOptions,
                        row.ccbField
                      );
                      const mcValueOption = resolveOptionByCode(
                        mcOptionsForRow,
                        row.mcMergeField,
                        row.mcDisplayName
                      );

                      return (
                        <div
                          key={index}
                          className="c-mc-hm__row d-flex align-items-start w-100"
                          style={{ gap: 16 }}
                        >
                          {/* CCB Field */}
                          <div
                            className="c-mc-hm__cell flex-grow-1"
                            style={{
                              width: 360,
                              minWidth: 360,
                              marginTop:
                                errorMessage[`${index}_mcMergeField`] &&
                                !errorMessage[`${index}_ccbField`]
                                  ? 25
                                  : 0,
                            }}
                          >
                            {index === 0 && (
                              <Typography className="mb-2" variant="body1">
                                <img
                                  className="mr-1  mb-1"
                                  src={CcbIcon}
                                  alt={"ccb-icon"}
                                />{" "}
                                Pushpay/CCB Custom Field
                              </Typography>
                            )}

                            {errorMessage[`${index}_ccbField`] && (
                              <span className="text-danger c-mc-hm__error">
                                {errorMessage[`${index}_ccbField`]}
                              </span>
                            )}

                            <Autocomplete
                              style={{ height: 40 }}
                              options={ccbFieldOptions}
                              value={ccbValueOption}
                              onChange={(e, opt) =>
                                handleRowChange(
                                  index,
                                  "ccbField",
                                  opt?.code || "Please Select"
                                )
                              }
                              groupBy={(option) => option.groupBy}
                              getOptionLabel={(option) => option?.name || ""}
                              loading={isCcbFieldsLoading}
                              disableClearable
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  placeholder="Select CCB merge field"
                                  error={!!errorMessage[`${index}_ccbField`]}
                                  className="w-100 select-box"
                                  size="small"
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {isCcbFieldsLoading ? (
                                          <CircularProgress size={18} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </div>

                          {/* MC Merge Field */}
                          <div
                            className="c-mc-hm__cell flex-grow-1"
                            style={{
                              width: 360,
                              minWidth: 360,
                              marginTop:
                                !errorMessage[`${index}_mcMergeField`] &&
                                errorMessage[`${index}_ccbField`]
                                  ? 25
                                  : 0,
                            }}
                          >
                            {index === 0 && (
                              <Typography className="mb-2" variant="body1">
                                <img
                                  className="mr-1 mb-1"
                                  src={MailchimpIcon}
                                  alt={"mc-icon"}
                                />{" "}
                                Mailchimp Merge Field/Tag
                              </Typography>
                            )}

                            {errorMessage[`${index}_mcMergeField`] && (
                              <span className="text-danger c-mc-hm__error">
                                {errorMessage[`${index}_mcMergeField`]}
                              </span>
                            )}

                            <Autocomplete
                              disabled={
                                !row.ccbField ||
                                row.ccbField === "Please Select"
                              }
                              key={`mc-${index}-${row.ccbField || "none"}`} // keep this
                              options={mcOptionsForRow}
                              value={mcValueOption}
                              onChange={(e, opt) =>
                                handleRowChange(
                                  index,
                                  "mcMergeField",
                                  opt?.code || "Please Select"
                                )
                              }
                              groupBy={(option) => option.groupBy}
                              renderGroup={(params) => (
                                <div key={params.key}>
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      padding: "8px 12px",
                                      color: "#000",
                                    }}
                                  >
                                    {params.group}
                                  </div>
                                  {params.children}
                                </div>
                              )}
                              getOptionLabel={(option) => option?.name || ""}
                              loading={isMergeFieldsLoading}
                              disableClearable
                              getOptionDisabled={(option) => {
                                // disable duplicates across rows (same as your old Select)
                                if (
                                  option?.code &&
                                  option.code !== row.mcMergeField
                                ) {
                                  return selectedMcFields.includes(option.code);
                                }
                                return false;
                              }}
                              renderOption={(option) => (
                                <span
                                  style={{
                                    color:
                                      option?.groupBy === GROUP_CREATE_DUP
                                        ? "red"
                                        : "inherit",
                                  }}
                                >
                                  {option?.name}{" "}
                                  {option?.groupBy === GROUP_CREATE_DUP
                                    ? "- Copy"
                                    : ""}
                                </span>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  variant="outlined"
                                  placeholder="Select MC merge field"
                                  error={
                                    !!errorMessage[`${index}_mcMergeField`]
                                  }
                                  className="w-100 select-box"
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {isMergeFieldsLoading ? (
                                          <CircularProgress size={18} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </div>

                          {/* Delete */}
                          <div
                            className="c-mc-hm__delete d-flex align-items-center justify-content-end "
                            style={{
                              width: 44,
                              paddingTop:
                                errorMessage[`${index}_mcMergeField`] ||
                                errorMessage[`${index}_ccbField`]
                                  ? 30
                                  : 10,
                            }}
                          >
                            <Delete
                              color="error"
                              titleAccess="Delete"
                              onClick={() => handleDeleteRow(index)}
                              style={{
                                cursor: "pointer",
                                marginTop: index === 0 ? 25 : 0,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add more */}
                  <div className="w-auto c-mc-hm__add-more mt-4 d-flex align-items-center">
                    <span
                      style={{
                        cursor: isMergeFieldsLoading
                          ? "not-allowed"
                          : "pointer",
                        opacity: isMergeFieldsLoading ? 0.6 : 1,
                      }}
                      onClick={isMergeFieldsLoading ? undefined : handleAddRow}
                    >
                      <Add fontSize="small" className="mr-1" /> Add More
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className="flexer-column mt-3">
          <div className="d-flex justify-content-between w-100">
            <MaterialButton
              title="Close"
              variant="outlined"
              className="w-30 ml-3 dark-gray-button"
              onClick={toggleModal}
            />

            <ButtonWithSpinner
              loading={isSaving}
              disabled={
                isSaving ||
                isInitialLoading ||
                !isAudienceSelected ||
                isMergeFieldsLoading ||
                rows.length === 0
              }
              className={"bg-yellow w-30 mr-3"}
              onClick={handleSave}
            >
              Save
            </ButtonWithSpinner>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = (store) => {
  return {
    mailChimpAudience: store.integrations.mailChimpAudience,
    mailChimpAudienceStatus: store.integrations.mailChimpAudienceStatus,

    mailchimpMergeFields: store.integrations.mailchimpMergeFields,
    mailchimpMergeFieldsStatus: store.integrations.mailchimpMergeFieldsStatus,

    ccbCustomFields: store.integrations.ccbCustomFields,
    ccbCustomFieldsStatus: store.integrations.ccbCustomFieldsStatus,

    customHeaderMappings: store.tools.customHeaderMappings,
    customHeaderMappingsStatus: store.tools.customHeaderMappingsStatus,

    saveMcCustomHeaderMappingStatus:
      store.tools.saveMcCustomHeaderMappingStatus,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getMailchimpAudience: (data) => dispatch(getMailchimpAudience(data)),
    getMailchimpMergeFields: (data) => dispatch(getMailchimpMergeFields(data)),
    getCcbCustomFields: (data) => dispatch(getCcbCustomFields(data)),
    getCustomHeaderMapping: (data) => dispatch(getCustomHeaderMapping(data)),
    setSnackbarData: (snackbarData) => dispatch(setSnackbarData(snackbarData)),
    saveMcCustomHeaderMapping: (data) =>
      dispatch(saveMcCustomHeaderMapping(data)),
    clearCustomMCHeaderMapping: () => dispatch(clearCustomMCHeaderMapping()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(CustomMailchimpHeaderMappingModal));
